import { Button, Card, Flex, Progress, Text } from "@radix-ui/themes";
import { Link as InertiaLink } from "@inertiajs/react";
import { BadgeCheck, ClipboardCheck, Printer } from "lucide-react";
import { ManagerEventDayOf } from "@/types/manager_events";

interface Props {
  dayOf: ManagerEventDayOf;
  checkInPath: string;
}

/**
 * Gate-day attendance. Rendered only when the server sends `day_of`, which it
 * does from the day of the event onward — judged in the *track's* time zone, so
 * a venue east of the app's zone flips over when its own day starts.
 *
 * The counts are over the same population the check-in screen lists (pending
 * and approved), so the two can't disagree about the denominator.
 */
export default function DayOfCard({ dayOf, checkInPath }: Props) {
  const { expected_count, attended_count, badge_count, media_list_path } =
    dayOf;

  return (
    <Card>
      <Flex direction="column" gap="2">
        <Flex align="center" gap="1">
          <ClipboardCheck size={14} />
          <Text size="2" weight="medium">
            Check-in
          </Text>
        </Flex>

        <Flex align="baseline" gap="2">
          <Text size="6" weight="bold">
            {attended_count}
          </Text>
          <Text size="2" color="gray">
            of {expected_count} checked in
          </Text>
        </Flex>
        <Progress
          value={
            expected_count > 0 ? (attended_count / expected_count) * 100 : 0
          }
        />

        <Flex align="center" gap="1">
          <BadgeCheck size={13} />
          <Text size="1" color="gray">
            {badge_count} {badge_count === 1 ? "badge" : "badges"} assigned
          </Text>
        </Flex>

        <Flex gap="2" mt="1">
          <Button asChild variant="soft" size="1">
            <InertiaLink href={checkInPath}>Check in participants</InertiaLink>
          </Button>
          {/* The paper fallback for a gate with no usable internet. Plain anchor:
              it's a PDF download, not an Inertia page. */}
          <Button asChild variant="soft" color="gray" size="1">
            <a href={media_list_path}>
              <Printer size={13} />
              Media list
            </a>
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
